import { createGlobalStyle } from 'styled-components';

import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { resolveTargetNodeId } from './review-utils';
import { useHighlightedFindingStore } from './use-highlighted-finding-store';

import { useNodes } from '../hooks/use-nodes';

// Keyed on ReactFlow's node wrapper (`data-id`) rather than threaded through the
// node components: one rule covers all 30-odd types and re-renders nothing.
// `outline` over `border` so nothing reflows; the viewport is left alone.
const NodeOutline = createGlobalStyle<{ $nodeId: string; $color: string }>`
  .react-flow__node[data-id="${(props) => props.$nodeId}"] {
    outline: ${pxToRem(2)} solid ${(props) => props.$color};
    outline-offset: ${pxToRem(4)};
    border-radius: ${pxToRem(12)};
  }
`;

export type HighlightedNode = {
  nodeId: string;
  color: Color;
};

/**
 * The node to outline for the hovered card, or null: nothing hovered, a field
 * finding (no node), or an action no longer on the canvas.
 */
export const useHighlightedNode = (): HighlightedNode | null => {
  const highlighted = useHighlightedFindingStore((state) => state.highlighted);
  const { getValidNodes } = useNodes();

  if (!highlighted) {
    return null;
  }

  const nodeId = resolveTargetNodeId(
    highlighted.target,
    getValidNodes().map((node) => ({ id: node.id, type: node.type }))
  );
  return nodeId ? { nodeId, color: highlighted.color } : null;
};

export const HighlightedNodeOutline = () => {
  const highlighted = useHighlightedNode();

  if (!highlighted) {
    return null;
  }

  return <NodeOutline $nodeId={highlighted.nodeId} $color={theme.color[highlighted.color]} />;
};
